import { h } from 'preact';
import { useEffect } from 'preact/compat';
import { useSelect } from 'downshift';
import FormError from './FormError';
import Caret from '../../icons/Caret';

type SelectOpt = {
	label: string;
	value: string;
}

// Get display label.
const itemToString = (item: SelectOpt) => (item ? item.label : '');

const CustomSelect = ({
	label,
	options,
	onChange,
	value,
	onTouch = null,
	defaultValue = false,
	hasError = false,
	autoComplete = false,
}: {
	label: string,
	options: SelectOpt[],
	value: string,
	hasError?: string | false,
	defaultValue?: SelectOpt | false,
	onChange: (item: any) => void,
	onTouch?: (e: FocusEvent) => void,
	autoComplete?: false | string,
}) => {
	/**
	 * Handle default Value.
	 * Only fire onMount.
	 */
	useEffect(() => {
		if (defaultValue) {
			onChange(defaultValue);
		}
	}, []);

	const {
		isOpen,
		selectedItem,
		getToggleButtonProps,
		getLabelProps,
		getMenuProps,
		highlightedIndex,
		getItemProps,
	} = useSelect({
		items: options,
		itemToString,
		selectedItem: options.filter((option) => option.value === value)[0] ?? { value: '', label: '' },
		onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
			onChange(newSelectedItem);
		},
	});

	return (
		<div
			className={[
				'ea-select',
				(hasError) ? 'error' : '',
			].join(' ')}
		>
			<div className="ea-select__inner">
				<div className="ea-select__top">
					<label className="ea-label" {...getLabelProps()}>
						{label}
					</label>
					<button
						aria-label="toggle menu"
						className="ea-select-button"
						type="button"
						onBlur={(e) => {
							if (onTouch) {
								onTouch(e);
							}
						}}
						{...getToggleButtonProps()}
					>
						<span>
							{selectedItem.label}
						</span>
						<span
							className={[
								'ea-select-button__caret',
								isOpen ? 'open' : 'closed',
							].join(' ')}
						>
							<Caret />
						</span>
					</button>
				</div>
				<ul
					{...getMenuProps()}
					className={[
						'ea-select__menu',
						isOpen ? 'open' : 'closed',
					].join(' ')}
				>
					{isOpen && options.map((item, index) => (
						<li
							className={[
								'ea-select-item',
								highlightedIndex === index ? 'highlight' : '',
								(selectedItem && selectedItem.value === item.value) ? 'selected' : '',
							].join(' ')}
							key={`${item.value}`}
							{...getItemProps({ item, index })}
						>
							<span>{item.label}</span>
						</li>
					))}
				</ul>
			</div>
			{/* Display error messages via children */}
			{hasError && (
				<FormError
					error={hasError}
					className="ea-select__error"
				/>
			)}
			{/**
			 * Hidden autocomplete field.
			 * - non-interactive
			 * - hidden from screen readers
			 * - hidden from view
			 * This is used to ensure the browser autofill works.
			 * Input cannot be declared 'hidden', as this disables autofill.
			 */}
			{autoComplete && (
				<div
					style={{
						overflow: 'hidden',
						height: '0',
						width: '0',
					}}
				>
					<input
						name={`${label}-auto`}
						style={{
							position: 'absolute',
							left: '-9999px',
							top: '0',
							touchAction: 'none',
							pointerEvents: 'none',
						}}
						tabIndex={-1}
						autoComplete={autoComplete}
						onChange={(e) => {
							const target = e.target as HTMLInputElement;
							// Update value if possible, must ensure value is in options.
							if (target.value !== '') {
								const opt = options.filter((option) => option.value === target.value)[0];
								if (opt) {
									onChange(opt);
								}
							}
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default CustomSelect;
